import {RefObject, useCallback, useEffect, useRef, useState} from "react";
import {
    readSurveyBlackoutSelection,
    SurveyBlackoutContainers,
    SurveyBlackoutSelection
} from "./surveyBlackoutOffsets";

/**
 * How long the selection has to hold still before it is read.
 *
 * WHY a quiet period instead of "read on mouseup": a double-click is not one event but a burst of
 * them — mousedown, mouseup, mousedown (which collapses the selection back to a caret), mouseup,
 * dblclick — and engines disagree about which of them leaves the word selected. Reacting to any
 * single event therefore either misses the word or cancels it a moment later. Every one of those
 * steps does fire `selectionchange`, so the result is derived from whatever the selection has
 * settled on once the burst is over: each change restarts this timer, and only the final state is
 * ever read. Short enough to feel immediate, long enough to swallow a double-click's internal
 * collapse-then-reselect.
 */
const SELECTION_SETTLE_MS = 90;

interface Options {
    /** The registered text containers, re-read on every settled read. */
    getContainers: () => SurveyBlackoutContainers
    /** The confirm bubble: a press inside it belongs to the selection, so it must not clear it. */
    ignoreRef?: RefObject<HTMLElement | null>
}

/**
 * The settled selection, and nothing else: this hook owns WHEN a selection is read, the caller owns
 * what to do with it. Document-level rather than per-container, because a drag routinely ends
 * outside the text it started in.
 */
export const useSurveyBlackoutSelection = ({getContainers, ignoreRef}: Options) => {
    const [selection, setSelection] = useState<SurveyBlackoutSelection | null>(null);

    // Latest containers without re-subscribing: the caller re-creates this closure every render.
    const containersRef = useRef(getContainers);
    containersRef.current = getContainers;

    const draggingRef = useRef(false);
    const settleRef = useRef<number | undefined>(undefined);

    const clearSelection = useCallback(() => setSelection(null), []);

    useEffect(() => {
        const readSettled = () => {
            // Mid-drag the selection is still growing — the mouseup below re-runs this once the
            // user has actually let go.
            if (draggingRef.current) return;
            setSelection(readSurveyBlackoutSelection(containersRef.current()));
        };

        const scheduleRead = () => {
            window.clearTimeout(settleRef.current);
            settleRef.current = window.setTimeout(readSettled, SELECTION_SETTLE_MS);
        };

        // Any press invalidates the standing selection: it belongs to a selection this press is
        // about to collapse. The one exception is the press on the bubble itself.
        const onMouseDown = (event: MouseEvent) => {
            draggingRef.current = true;
            const target = event.target as Node | null;
            const ignored = ignoreRef?.current;
            if (target && ignored && ignored.contains(target)) return;
            setSelection(null);
        };

        const onMouseUp = () => {
            draggingRef.current = false;
            scheduleRead();
        };

        document.addEventListener("selectionchange", scheduleRead);
        document.addEventListener("mousedown", onMouseDown);
        document.addEventListener("mouseup", onMouseUp);

        return () => {
            window.clearTimeout(settleRef.current);
            document.removeEventListener("selectionchange", scheduleRead);
            document.removeEventListener("mousedown", onMouseDown);
            document.removeEventListener("mouseup", onMouseUp);
        };
    }, [ignoreRef]);

    return {selection, clearSelection};
};
